import React, {useEffect, useState} from 'react';
import { Paper, Button, FormControl, Select } from '@mui/material';
import { styled } from "@mui/system";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import { useDropzone } from 'react-dropzone';
import {
    calcAddressByOffset, calcChecksum,
    calcOffset,
    getAddressByPatchNumber,
    getAddressBytesByString,
    getPatchNumberByAddressString
} from "./utils";
import MidiOutSelector from "./MidiOutSelector";
import DeviceIdSelector from "./DeviceIdSelector";
import {Overlay} from "./Overlay";

const DropZone = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '30px solid',
    borderColor: 'rgba(127, 255, 127, 1)',
    padding: '10px',
    margin: '20px',
    borderRadius: '100px',
    fontSize: '20px',
    height: '50%',
    whiteSpace: 'pre-line',
});
export default function LoadPatch({ midiOutputs }) {
    const onDrop = (acceptedFiles) => {
        if ((!loadedAllPatchData && acceptedFiles.length > 1) || Object.keys(loadedSinglePatchData).length > 0 || !loadedAllPatchData)
        {
            acceptedFiles.forEach((droppedFile) => {
                checkFileFormat(droppedFile);
            });
        }
    };

    const [loadedSinglePatchData, setLoadedSinglePatchData] = useState({});
    const [loadedAllPatchData, setLoadedAllPatchData] = useState(null);
    const { getRootProps } = useDropzone({　onDrop });
    const [midiOut, setMidiOut] = useState(null);
    const [midiOutId, setMidiOutId] = useState(() => {
        const savedData = localStorage.getItem('midiOutILoad');
        return savedData ? JSON.parse(savedData) : null;
    });
    const [deviceId, setDeviceId] = useState(() => {
        const savedData = localStorage.getItem('deviceIdLoad');
        const number = parseInt(savedData ? JSON.parse(savedData) : 1);
        return number >= 0 && number < 32 ? number : 0;
    });
    const [allPatchSendPercent, setAllPatchSendPercent] = useState(0);

    useEffect(() => {
        localStorage.setItem('midiOutILoad', JSON.stringify(midiOutId));
    }, [midiOutId]);

    useEffect(() => {
        localStorage.setItem('deviceIdLoad', JSON.stringify(deviceId));
    }, [deviceId]);

    const checkFileFormat = (file) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const fileContent = e.target.result;
            handleFileContent(file.name, fileContent);
        };

        reader.readAsText(file);
    };

    const handleFileContent = (filename, content) => {
        const patchData = JSON.parse(content);
        const keys = Object.keys(patchData);
        if (keys.length > 0) {
            const firstKey = keys[0];
            if (firstKey === 'UA1') {
                setLoadedAllPatchData({
                    filename: filename,
                    data: patchData,
                });
            } else {
                const firstPatchNumber = getPatchNumberByAddressString(firstKey);
                if (!firstPatchNumber.startsWith('U')) {
                    return;
                }
                const patchNumber = getPatchNumberByAddressString(firstKey);
                const bank = patchNumber.slice(0, 2);
                const patch = patchNumber.slice(2);
                setLoadedSinglePatchData((preLoadedPatchData) => {
                    return {
                        ...preLoadedPatchData,
                        [filename]: {
                            orgPatchNumber: patchNumber,
                            bank: bank,
                            patch: patch,
                            data: patchData,
                        },
                    }
                });
            }
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleClear = () => {
        setLoadedSinglePatchData({});
        setLoadedAllPatchData(null);
    }
    
    const handleWritePatchData = async () => {
        if (midiOut === null) {
            return;
        }

        const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));
        if (Object.keys(loadedSinglePatchData).length > 0) {
            const values = Object.values(loadedSinglePatchData);
            let counter = 0;
            for (const value of values) {
                const orgAddress = getAddressByPatchNumber(value.orgPatchNumber);
                const patchAddress = getAddressByPatchNumber(value.bank + value.patch);
                const data = value.data;
                const keys = Object.keys(data);
                counter++;
                setAllPatchSendPercent(Math.floor(counter / values.length * 100));

                for (const key of keys) {
                    const offset = calcOffset(orgAddress, getAddressBytesByString(key));
                    const address = calcAddressByOffset(patchAddress, offset);
                    const contentData = Object.values(data[key]);
                    const dataSetMessage = [0xF0, 0x41, deviceId, 0x00, 0x23, 0x12, ...address, ...contentData, calcChecksum([...address, ...contentData]), 0xF7];
                    midiOut.send(dataSetMessage);
                    await sleep(100);
                }
            }
        } else if (loadedAllPatchData) {
            const values = Object.values(loadedAllPatchData.data);
            let counter = 0;
            for (const value of values) {
                const keys = Object.keys(value);
                counter++;
                setAllPatchSendPercent(Math.floor(counter / values.length * 100));

                for (const key of keys) {
                    const address = getAddressBytesByString(key);
                    const contentData = Object.values(value[key]);
                    const dataSetMessage = [0xF0, 0x41, deviceId, 0x00, 0x23, 0x12, ...address, ...contentData, calcChecksum([...address, ...contentData]), 0xF7];
                    midiOut.send(dataSetMessage);
                    await sleep(100);
                }
            }
        }
        if (Object.keys(loadedSinglePatchData).length > 0 || loadedAllPatchData) {
            const patchWriteRequestMessage = [0xF0, 0x41, deviceId, 0x00, 0x23, 0x12, 0x70, 0x00, 0x00, 0x00, 0x00, 0x10, 0xF7];
            midiOut.send(patchWriteRequestMessage);
            alert("PatchデータをVF-1に送信完了しました。VF-1がBULK LOADになっていれば書き込みしていますのでVF-1の表示が書き込み完了時に短時間「Keep Power ON!」の表示となった後「NOW WAITING...」に戻るまでお待ちください。現在選択されているパッチデータを書き込んだ場合は別のパッチデータに一度切り替えて戻す事で変更が反映されます。")
            setAllPatchSendPercent(0);
        }
    }


    return (
        <Paper
            onDragOver={handleDragOver}
            style={{ height: 'calc(100vh - 48px)', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <DropZone {...getRootProps()}>
                {"Patchデータファイルをドロップ\nまたはクリックしてファイルを開く\n(単体Patchデータは複数ファイル読み込み可能)\n\n全Patchデータを指定する場合は単体ファイルのみ"}
            </DropZone>
            <FormControl style={{ height: '100%', overflow: 'auto', maxHeight: 'calc(100vh - 100px)' }}>
                {(Object.keys(loadedSinglePatchData).length > 0 || loadedAllPatchData) && (
                    <div>
                        <MidiOutSelector setMidiOut={setMidiOut} initialId={midiOutId} setInitialId={setMidiOutId} midiOutputs={midiOutputs}/>
                        <DeviceIdSelector deviceId={deviceId} setCDeviceId={setDeviceId}/>
                        <Button variant="contained" color="primary" sx={{ textTransform: 'none' }} onClick={handleClear} style={{ width: '90%', margin: '10px', }}>
                            Patchデータファイル読み込みクリア
                        </Button>
                    </div>
                )}
                {Object.keys(loadedSinglePatchData).length > 0 && (
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={4}>
                                    単体パッチデータ 書き込み先
                                </TableCell>
                            </TableRow>
                            {Object.keys(loadedSinglePatchData).map((key) => (
                                <TableRow>
                                    <TableCell>{key}</TableCell>
                                    <TableCell>→</TableCell>
                                    <TableCell>
                                        <FormControl variant="outlined" sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center'}}>
                                            <InputLabel id="bank-label">Bank</InputLabel>
                                            <Select
                                                labelId="bank-label"
                                                value={loadedSinglePatchData[key].bank}
                                                onChange={(e) => setLoadedSinglePatchData({
                                                    ...loadedSinglePatchData,
                                                    [key]: {
                                                        ...loadedSinglePatchData[key],
                                                        bank: e.target.value,
                                                    }
                                                })}
                                                label="Bank"
                                            >
                                                <MenuItem value={'UA'}>UA</MenuItem>
                                                <MenuItem value={'UB'}>UB</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            value={loadedSinglePatchData[key].patch}
                                            onChange={(e) => setLoadedSinglePatchData({
                                                ...loadedSinglePatchData,
                                                [key]: {
                                                    ...loadedSinglePatchData[key],
                                                    patch: e.target.value,
                                                }
                                            })}
                                            type="number"
                                            label="Patch"
                                            variant="outlined"
                                            sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                                            style={{ marginRight: '10px' }}
                                            InputProps={{ inputProps: { min: 0, max: 101 } }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {loadedAllPatchData && (
                    <div style={{ marginTop: '30px', marginBottom: '20px', }}>
                        <div style={{ fontSize: '20px', fontWeight: "bold", }}>
                            全パッチデータ一括書き込み
                        </div>
                        {loadedAllPatchData.filename}
                    </div>
                )}
                {(Object.keys(loadedSinglePatchData).length > 0 || loadedAllPatchData) && (
                    <div>
                        VF-1をBULK LOADモードにしてから書き込んでください。
                        <Button variant="contained" color="secondary" sx={{ textTransform: 'none' }} onClick={handleWritePatchData} disabled={!midiOut}>
                            {Object.keys(loadedSinglePatchData).length > 1 && "一括"}書き込み
                        </Button>
                    </div>
                )}
            </FormControl>
            {allPatchSendPercent > 0 && <Overlay>{loadedAllPatchData ? '全' : '単体'}パッチデータ送信中... {allPatchSendPercent + "%"}</Overlay>}
        </Paper>
    );
}